/**
 * Shared motion configuration and utilities
 * Respects prefers-reduced-motion user preference
 */

import { Transition, Variants } from "framer-motion";

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Standard transition timing
 */
export const transitions = {
  fast: {
    duration: 0.15,
    ease: "easeOut",
  } as Transition,
  standard: {
    duration: 0.2,
    ease: "easeOut",
  } as Transition,
  slow: {
    duration: 0.3,
    ease: "easeOut",
  } as Transition,
} as const;

/**
 * Get transition with reduced motion support
 */
export const getTransition = (
  transition: Transition = transitions.standard,
): Transition => {
  if (prefersReducedMotion()) {
    return { duration: 0.01 };
  }
  return transition;
};

/**
 * Button/Link press animation
 */
export const pressVariants: Variants = {
  initial: { scale: 1 },
  tap: { scale: 0.98 },
};

/**
 * Card hover animation
 */
export const cardHoverVariants: Variants = {
  initial: { y: 0 },
  hover: {
    y: -2,
    transition: getTransition(transitions.standard),
  },
};

/**
 * Fade in with slight rise animation for page content
 */
export const fadeInUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: getTransition(transitions.standard),
  },
};

/**
 * Stagger children animation
 */
export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.02,
    },
  },
};

/**
 * Stagger item animation
 */
export const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: getTransition(transitions.standard),
  },
};
